/**
 * AddressAutocompleteUI Theme
 *
 * Theme configuration for the AddressAutocompleteUI component.
 * This component displays address autocomplete predictions as a floating dropdown.
 *
 * Parts:
 * - container: The outer dropdown container (positioned absolutely)
 * - list: The VStack wrapper for prediction items
 * - item: Individual prediction item (clickable)
 * - mainText: Primary text (street/place name)
 * - secondaryText: Secondary text (city, state, country)
 */

export default {
  parts: ['container', 'list', 'item', 'mainText', 'secondaryText'],

  baseStyle: {
    container: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      zIndex: 1000,
      bg: 'white',
      border: '1px solid',
      borderColor: 'gray.200',
      borderRadius: 'md',
      maxH: '300px',
      overflowY: 'auto',
      shadow: 'lg',
      mt: 1,
    },

    list: {
      align: 'stretch',
      spacing: 0,
      width: '100%',
    },

    item: {
      borderBottom: '1px solid',
      borderColor: 'gray.100',
      cursor: 'pointer',
      p: 3,
      transition: 'background 0.2s',
      width: '100%',

      _hover: {
        bg: 'gray.100',
      },

      _last: {
        borderBottom: 'none',
      },
    },

    mainText: {
      fontSize: 'sm',
      fontWeight: 'medium',
      color: 'gray.900',
    },

    secondaryText: {
      fontSize: 'xs',
      color: 'gray.600',
      mt: 0.5,
    },
  },

  variants: {
    /**
     * Default variant - standard styling
     */
    default: {},

    /**
     * Compact variant - smaller padding and text
     */
    compact: {
      container: {
        maxH: '250px',
      },
      item: {
        p: 2,
      },
      mainText: {
        fontSize: 'xs',
      },
      secondaryText: {
        fontSize: '2xs',
      },
    },

    /**
     * Elevated variant - more prominent shadow
     */
    elevated: {
      container: {
        shadow: 'xl',
        borderColor: 'gray.300',
      },
    },

    /**
     * Bordered variant - thicker border, no shadow
     */
    bordered: {
      container: {
        border: '2px solid',
        borderColor: 'gray.300',
        shadow: 'none',
        borderRadius: 'lg',
      },
    },

    /**
     * PDP delivery row 
     */
    pdp: {
      container: {
        border: 'none',
        borderRadius: '4px',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 2px 6px 2px rgba(0, 0, 0, 0.15)',
        left: 0,
        maxH: 'min(320px, 50vh)',
        mt: 2,
        overflowY: 'auto',
        p: 0,
        py: 2,
        position: 'absolute',
        right: 'auto',
        top: '80%',
        w: '100%',
        zIndex: 1000,
      },

      list: {
        align: 'stretch',
        spacing: 0,
        width: '100%',
      },

      item: {
        _hover: {
          bg: 'brandColors.secondaryBG',
        },
        alignItems: 'center',
        bg: 'white',
        border: 'none',
        borderBottom: 'none',
        cursor: 'pointer',
        display: 'flex',
        gap: 4,
        px: 4,
        py: 4,
        transition: 'background 0.15s ease-out',
        width: '100%',
      },

      mainText: {
        color: '#1C1C1C',
        fontFamily: 'body',
        fontSize: '16px',
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: 'normal',
        lineHeight: '1.4',
        sx: {
          fontVariantNumeric: 'lining-nums proportional-nums',
        },
      },

      secondaryText: {
        display: 'none',
      },
    },
  },

  sizes: {
    sm: {
      container: {
        maxH: '200px',
      },
      item: {
        p: 2,
      },
      mainText: {
        fontSize: 'xs',
      },
      secondaryText: {
        fontSize: '2xs',
      },
    },

    md: {
      container: {
        maxH: '300px',
      },
      item: {
        p: 3,
      },
      mainText: {
        fontSize: 'sm',
      },
      secondaryText: {
        fontSize: 'xs',
      },
    },

    lg: {
      container: {
        maxH: '400px',
      },
      item: {
        p: 4,
      },
      mainText: {
        fontSize: 'md',
      },
      secondaryText: {
        fontSize: 'sm',
      },
    },
  },

  defaultProps: {
    variant: 'default',
    size: 'md',
  },
};
